import {
  CellTemplate,
  Compatible,
  Uncertain,
  Cell,
  UncertainCompatible
} from '@silevis/reactgrid'
import { Input } from '../../../../components/Input'
import { PROPS_INPUT_NUMBER } from '../../../ContractDetails/RebateConditions/RebateConditionsForm/constants'
import { useEffect, useState } from 'react'

export const normalizeNumberValue = (
  rawValue: unknown,
  min?: string
): number => {
  if (rawValue == null || rawValue === '') {
    return NaN
  }
  const num = Number(rawValue)
  if (Number.isNaN(num) || (min != null && num < Number(min))) {
    return NaN
  }
  return num
}

export interface NumberCellInterface extends Cell {
  type: 'number'
  value: number
  key?: string
  placeholder: string
  focusCallback?: () => void
  unFocusCallback?: () => void
  isError?: boolean
  disabled?: boolean
  max?: string
  min?: string
  parser?: (value: string | undefined) => string
}

export class CustomCellNumberTemplate
  implements CellTemplate<NumberCellInterface>
{
  getCompatibleCell(
    uncertainCell: Uncertain<NumberCellInterface>
  ): Compatible<NumberCellInterface> {
    const value = normalizeNumberValue(uncertainCell.value, uncertainCell.min)
    const placeholder = uncertainCell.placeholder
      ? uncertainCell.placeholder
      : ''
    return {
      ...uncertainCell,
      value,
      placeholder,
      text: Number.isNaN(value) ? '' : value.toString()
    }
  }

  update(
    cell: Compatible<NumberCellInterface>,
    cellToMerge: UncertainCompatible<NumberCellInterface>
  ): Compatible<NumberCellInterface> {
    return this.getCompatibleCell({ ...cell, value: cellToMerge.value })
  }

  render(
    cell: Compatible<NumberCellInterface>,
    _isInEditMode: boolean,
    onCellChanged: (
      cell: Compatible<NumberCellInterface>,
      commit: boolean
    ) => void
  ) {
    return (
      <InputPicker
        onChangedVal={(val) =>
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              value: val
            }),
            true
          )
        }
        {...cell}
      />
    )
  }

  isFocusable() {
    return true
  }
}

const InputPicker = ({
  isError,
  value,
  placeholder,
  focusCallback,
  unFocusCallback,
  onChangedVal,
  max,
  min,
  parser,
  disabled
}: Compatible<NumberCellInterface> & {
  onChangedVal: (val: number) => void
}) => {
  const [numValue, setValue] = useState<number | null>(null)

  useEffect(() => {
    setValue(Number.isNaN(value) ? null : value)
  }, [value])

  const getCommittedValue = (val: number | null): number =>
    normalizeNumberValue(val, min)

  return (
    <Input.NumberNoForm
      hasCustomError={isError}
      propsInputNumber={{
        value: numValue ?? undefined,
        ...PROPS_INPUT_NUMBER,
        placeholder: placeholder,
        max,
        parser: parser ? parser : PROPS_INPUT_NUMBER['parser'],
        maxLength: 64,
        disabled: disabled,
        onCopy: (e) => e.stopPropagation(),
        onCut: (e) => e.stopPropagation(),
        onPointerDown: (e) => e.stopPropagation(),
        onPaste: (e) => e.preventDefault(),
        onChange: (e) => setValue(e == null || e === '' ? null : Number(e)),
        onFocus: () => focusCallback?.(),
        onBlur: () => {
          const committed = getCommittedValue(numValue)
          if (Number.isNaN(committed)) {
            setValue(null)
          }
          onChangedVal(committed)
          unFocusCallback?.()
        }
      }}
    />
  )
}
